import {
  Epoch,
  ScoringBand,
  StudyRunCaseDashboardGateStatus,
  type StudyRunCaseDashboardThreadVerdictDto,
  type StudyRunCaseSelectDto,
  type StudyScoringOutputSelectDto,
  StudyThreadRole,
  type StudyValidationResultDto,
} from '@@types/api.types';
import {
  dashboardScoreFromBand,
  normalizeDashboardScore as sharedNormalizeDashboardScore,
} from '@azymmetric/shared/types/meia/scoring.types';
import type { BadgeProps } from '@components/ui/badge';

const EPOCH_LABELS: Readonly<Record<Epoch, string>> = {
  [Epoch.BEYOND]: 'Beyond',
  [Epoch.FYDP]: 'FYDP',
  [Epoch.NEAR_TERM]: 'Near Term',
};

export const formatEpochLabel = (epoch: Epoch): string => EPOCH_LABELS[epoch] ?? epoch;

const GATE_STATUS_LABELS: Readonly<Record<StudyRunCaseDashboardGateStatus, string>> = {
  [StudyRunCaseDashboardGateStatus.FAIL]: 'Fail',
  [StudyRunCaseDashboardGateStatus.PASS]: 'Pass',
  [StudyRunCaseDashboardGateStatus.PENDING]: 'Pending',
  [StudyRunCaseDashboardGateStatus.SOFT]: 'Soft',
};

export const getGateStatusLabel = (status: StudyRunCaseDashboardGateStatus): string => GATE_STATUS_LABELS[status];

export type GateStatusTone = {
  badgeVariant: BadgeProps['variant'];
  textClass: string;
};

const GATE_STATUS_TONES: Readonly<Record<StudyRunCaseDashboardGateStatus, GateStatusTone>> = {
  [StudyRunCaseDashboardGateStatus.FAIL]: { badgeVariant: 'destructive', textClass: 'text-red-700' },
  [StudyRunCaseDashboardGateStatus.PASS]: { badgeVariant: 'success', textClass: 'text-green-700' },
  [StudyRunCaseDashboardGateStatus.PENDING]: { badgeVariant: 'pending', textClass: 'text-gray-500' },
  [StudyRunCaseDashboardGateStatus.SOFT]: { badgeVariant: 'warning', textClass: 'text-yellow-700' },
};

export const getGateStatusTone = (status?: StudyRunCaseDashboardGateStatus): GateStatusTone =>
  status ? GATE_STATUS_TONES[status] : GATE_STATUS_TONES[StudyRunCaseDashboardGateStatus.PENDING];

type FormatRunCaseLabelParams = {
  includeEpoch?: boolean;
  runCase: Pick<StudyRunCaseSelectDto, 'epoch' | 'version'>;
};

// Single source of truth for run-case labels across P4 + P5 + the API mapper.
// Default form is `Run ${version}`. With `includeEpoch: true`, returns
// `v${version} · ${epoch label}` for the P5 brief and similar dense surfaces.
export const formatRunCaseLabel = (params: FormatRunCaseLabelParams): string => {
  const { includeEpoch = false, runCase } = params;
  if (includeEpoch) return `v${runCase.version} · ${formatEpochLabel(runCase.epoch)}`;
  return `Run ${runCase.version}`;
};

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const UUID_V4_PATTERN = /^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i;

type GetLatestScoringOutputByRunCaseIdParams = {
  runCases: StudyRunCaseSelectDto[];
  scoringOutputs: StudyScoringOutputSelectDto[];
};

export type ValidationResultsSummary = {
  blockingCount: number;
  infoCount: number;
  totalCount: number;
  warningCount: number;
};

export const formatRunCaseDate = (value?: string | null): string => {
  if (!value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';

  return DATE_FORMATTER.format(date);
};

export const formatDashboardScore = (score?: number | null): string => {
  const normalizedScore = normalizeDashboardScore(score);
  return normalizedScore === null ? '—' : `${normalizedScore}`;
};

export const getDefaultActiveThreadId = (threads: StudyRunCaseDashboardThreadVerdictDto[]): string | null => {
  const primaryThread = threads.find((thread) => thread.threadRole === StudyThreadRole.PRIMARY);
  return primaryThread?.threadId ?? threads[0]?.threadId ?? null;
};

export const normalizeDashboardScore = (score?: number | null): number | null => sharedNormalizeDashboardScore(score);

export const scoreFromBand = (band?: ScoringBand | null): number | null => dashboardScoreFromBand(band);

export const hasValidRunCaseId = (runCase: StudyRunCaseSelectDto): boolean => UUID_V4_PATTERN.test(runCase.id);

const isOutputNewer = (candidate: StudyScoringOutputSelectDto, current: StudyScoringOutputSelectDto): boolean =>
  new Date(candidate.scoredAt).getTime() > new Date(current.scoredAt).getTime();

export const getLatestScoringOutputByRunCaseId = (
  params: GetLatestScoringOutputByRunCaseIdParams,
): Map<string, StudyScoringOutputSelectDto> => {
  const { runCases, scoringOutputs } = params;
  const runCaseIds = new Set(runCases.map((runCase) => runCase.id));
  const outputsByRunCaseId = new Map<string, StudyScoringOutputSelectDto>();

  for (const output of scoringOutputs) {
    if (!runCaseIds.has(output.runCaseId)) continue;
    const current = outputsByRunCaseId.get(output.runCaseId);
    if (!current || isOutputNewer(output, current)) outputsByRunCaseId.set(output.runCaseId, output);
  }

  return outputsByRunCaseId;
};

export const summarizeValidationResults = (results: StudyValidationResultDto[]): ValidationResultsSummary => {
  const summary: ValidationResultsSummary = {
    blockingCount: 0,
    infoCount: 0,
    totalCount: results.length,
    warningCount: 0,
  };

  for (const result of results) {
    if (result.blocking) summary.blockingCount += 1;
    if (result.severity === 'WARNING') summary.warningCount += 1;
    if (result.severity === 'INFO') summary.infoCount += 1;
  }

  return summary;
};
